import React, { useContext, useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Footer from '@components/footer/Footer';
import { NavContext } from '@components/navigation/Context';
import SEOTags from '@components/SEOTags';
import Colors from '@components/shared/Colors';
import Zendesk from '@components/shared/Zendesk';
import { PayPalProvider } from '@contexts/PayPalContext';
import { SEOProps } from '@contracts';
import { blockDevScripts } from '@utils/blockDevScripts';
import { pageIsAccountPage, pageIsFocusPage, pageIsQuestionnairePage } from '@utils/helpers';
import { loadThirdPartyScripts } from '@utils/loadScripts';
import { pageViewTrack } from '@utils/tracking';
import AcceptTerms from './authentication/AcceptTerms';
import VMSFooter from './dashboard/components/VMSFooter';
import StatusBar from './LayoutStatusBar';
import { ModalProvider } from './ModalContext';
import Nav from './navigation/Nav';
import RegionSwitcherModal from './navigation/RegionSwitcherModal';
import SiteBanner from './shared/SiteBanner';
import '@src/sass/main.scss';
import '@algolia/autocomplete-theme-classic';

interface Props {
  children: React.ReactNode;
  style?: any;
  SEO?: SEOProps;
  isVMS?: boolean;
  showNav?: boolean;
}

export default function Layout({ children, style, SEO, isVMS = false, showNav = true }: Props) {
  const { state } = useContext(Context);
  const { firebaseAuth } = state;
  const { navDispatch } = useContext(NavContext);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const isAccountPage = pageIsAccountPage(location.pathname);
  const isFocusPage = pageIsFocusPage(location.pathname);
  const isQuestionnairePage = pageIsQuestionnairePage(location.pathname);

  useEffect(() => {
    // Initialize dev script blocker
    // blockDevScripts();

    let initialColor = Colors.forestGreen;
    let initialNavbarColor: string = Colors.cream;

    if (isFocusPage) {
      initialColor = Colors.navyBlue;
      initialNavbarColor = 'transparent';
    }

    if (isAccountPage) {
      initialColor = Colors.navyBlue;
    }

    navDispatch({ type: 'setColor', data: initialColor });
    navDispatch({ type: 'setNavbarColor', data: initialNavbarColor });

    (async () => {
      await loadThirdPartyScripts(location);
    })();

    if (document) {
      if (isAccountPage) {
        document.body.classList.add('vms-page');
      } else {
        document.body.classList.remove('vms-page');
      }
      if (isFocusPage) {
        document.body.classList.add('vms-page-focus');
      } else {
        document.body.classList.remove('vms-page-focus');
      }
      if (isQuestionnairePage) {
        document.body.classList.add('questionnaire-page');
      } else {
        document.body.classList.remove('questionnaire-page');
      }
    }
    setLoaded(true);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'optimize.activate' });

    return () => {
      document.body.classList.remove('vms-page');
      document.body.classList.remove('vms-page-focus');
      document.body.classList.remove('questionnaire-page');
    };
  }, []);

  useEffect(() => {
    if (firebaseAuth) {
      const { currentUser } = firebaseAuth;
      (async () => {
        await pageViewTrack(currentUser);
      })();
    }
  }, [firebaseAuth?.currentUser]);

  const renderFooter = () => {
    if (isFocusPage) return null;
    if (isVMS) return <VMSFooter />;
    return <Footer />;
  };

  return (
    <CookiesProvider>
      <ModalProvider>
        <PayPalProvider>
          <LayoutWrapper id="page-wrapper" style={style} $loaded={loaded}>
            <Zendesk />
            {SEO && <SEOTags SEO={SEO} />}
            <SiteBanner />
            <StatusBar />
            {showNav && <Nav />}
            <Content>{children}</Content>
            {renderFooter()}
            <AcceptTerms closeable />
          </LayoutWrapper>
          <RegionSwitcherModal />
        </PayPalProvider>
      </ModalProvider>
    </CookiesProvider>
  );
}

interface ILayoutWrapper {
  $loaded: boolean;
}

const LayoutWrapper = styled.div<ILayoutWrapper>`
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  opacity: 1;

  @media (max-width: 960px) {
    min-height: 100vh;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
